import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ServiceCard } from '../components/ServiceCard';
import { 
  Home, 
  RefreshCw, 
  TrendingUp, 
  Users, 
  Building2, 
  CheckCircle2, 
  Star,
  ArrowRight,
  Shield,
  Clock,
  HandCoins
} from 'lucide-react';

const services = [
  {
    icon: Home,
    title: 'Home Loans',
    description: 'Find the perfect home loan to match your needs. We compare rates from 40+ lenders to get you the best deal.',
    features: ['Variable & fixed rates', 'Low deposit options', 'Offset accounts']
  },
  {
    icon: RefreshCw,
    title: 'Refinancing',
    description: 'Could you be paying less? We help you refinance to a better rate and potentially save thousands.',
    features: ['Rate comparison', 'Equity release', 'Debt consolidation']
  },
  {
    icon: TrendingUp,
    title: 'Investment Loans',
    description: 'Build your property portfolio with competitive investment loan options tailored for investors.',
    features: ['Interest-only options', 'Tax benefits advice', 'Portfolio planning']
  },
  {
    icon: Users,
    title: 'First Home Buyer',
    description: "Take your first step onto the property ladder. We'll guide you through grants, schemes, and the best first home buyer loans.",
    features: ['FHOG assistance', 'Stamp duty savings', 'Low deposit loans']
  },
  {
    icon: Building2,
    title: 'Commercial Loans',
    description: 'Finance your business premises or commercial property investment with tailored solutions.',
    features: ['Business premises', 'Commercial property', 'Development finance']
  }
];

const testimonials = [
  {
    name: 'Sarah & Michael',
    location: 'Sydney, NSW',
    text: "Loans & Beyond made buying our first home so much easier. They found us a great rate and held our hand through the entire process.",
    rating: 5
  },
  {
    name: 'James Chen',
    location: 'Melbourne, VIC',
    text: "Refinancing with Loans & Beyond saved us over $400 a month. The team was professional and the process was seamless.",
    rating: 5
  },
  {
    name: 'Amanda Roberts',
    location: 'Brisbane, QLD',
    text: "As a property investor, I appreciate their expertise in finding the right loan structure for my portfolio. Highly recommend!",
    rating: 5
  }
];

const whyChooseUs = [
  {
    icon: HandCoins,
    title: 'No Cost to You',
    description: 'Our service is completely free. Lenders pay us, not you.'
  },
  {
    icon: Shield,
    title: '40+ Lenders',
    description: 'Access to over 40 lenders means more options and better rates.'
  },
  {
    icon: Clock,
    title: 'Fast Approvals',
    description: 'We streamline the process to get you approved faster.'
  }
];

export default function HomePage() {
  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section className="min-h-[80vh] relative bg-gradient-to-br from-gray-50 to-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-[#6B7280]/10 text-[#6B7280] rounded-full text-sm font-medium mb-6">
                Trusted Australian Mortgage Broker
              </span>
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-[#374151] leading-none mb-6">
                Your Home<br />
                <span className="text-[#6B7280]">Loan Journey</span><br />
                Starts Here
              </h1>
              <p className="text-lg md:text-xl text-gray-500 leading-relaxed mb-8 max-w-lg">
                We compare home loans from over 40 lenders to find you the best rate. 
                Expert guidance, no cost to you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button 
                    size="lg"
                    className="bg-[#6B7280] hover:bg-[#4B5563] text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                    data-testid="hero-cta-primary"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-2 border-[#6B7280] text-[#6B7280] hover:bg-[#6B7280] hover:text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-300"
                    data-testid="hero-cta-secondary"
                  >
                    Our Services
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right Image - Happy Family */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1758523671087-b256bbbca475?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGZhbWlseSUyMG1vdmluZyUyMG5ldyUyMGhvbWUlMjBib3hlcyUyMGtleXN8ZW58MHx8fHwxNzcwMDA2MTc5fDA&ixlib=rb-4.1.0&q=85"
                  alt="Happy couple moving into new home"
                  className="w-full h-[500px] object-cover"
                  data-testid="hero-image"
                />
              </div>
              {/* Floating Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-[#6B7280]" />
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-[#374151]">Quick Approvals</p>
                    <p className="text-sm text-gray-500">Get pre-approved in 24hrs</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#6B7280] font-medium text-sm tracking-wide uppercase">Why Choose Us</span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#374151] mt-4">
              Making Home Loans Simple
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8"
              >
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-[#6B7280]" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-[#374151] mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-500">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 bg-gray-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#6B7280] font-medium text-sm tracking-wide uppercase">Our Services</span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#374151] mt-4">
              Comprehensive Lending Solutions
            </h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
              From your first home to your investment portfolio, we have the expertise to help you achieve your property goals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {services.map((service, index) => (
              <ServiceCard
                key={index}
                {...service}
                delay={index * 0.1}
              />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/services">
              <Button 
                variant="outline"
                className="border-2 border-[#6B7280] text-[#6B7280] hover:bg-[#6B7280] hover:text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-300"
                data-testid="view-all-services"
              >
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 md:py-32 bg-[#6B7280]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-white/70 font-medium text-sm tracking-wide uppercase">Testimonials</span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mt-4">
              What Our Clients Say
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-2xl p-8"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-white text-white" />
                  ))}
                </div>
                <p className="text-white/80 leading-relaxed mb-6">"{testimonial.text}"</p>
                <div>
                  <p className="font-heading font-semibold text-white">{testimonial.name}</p>
                  <p className="text-white/50 text-sm">{testimonial.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-gray-100" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#374151] mb-6">
              Ready to Find Your Perfect Home Loan?
            </h2>
            <p className="text-lg text-gray-500 mb-8 max-w-2xl mx-auto">
              Get in touch today for a free consultation. No obligations, just expert advice tailored to your situation.
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-[#6B7280] hover:bg-[#4B5563] text-white rounded-full px-10 py-7 text-lg font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                data-testid="cta-button"
              >
                Book Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
